import { useMemo, type PropsWithChildren, type ReactElement } from 'react';

import { ChartSeriesActionContext } from '../../../core/components/series-action/ChartSeriesActionContext.js';
import { FormatterContext } from '../../shared/context/FormatterContext.js';
import { MeterBarChartDataProvider } from '../context/MeterBarChartDataContext.js';
import { NAME_DEFAULT_VALUE, SEGMENT_COLOR_DEFAULT } from '../types/default.js';
import type {
  MeterBarChartDataProviderData,
  MeterBarChartInternalConfig,
} from '../types/meter-bar-chart-internal.js';
import type { MeterBarChartProps } from '../types/meter-bar-chart.js';

interface MeterBarChartProvidersProps {
  containerWidth: number;
  meterBarChartInternal: MeterBarChartInternalConfig;
  meterBarChartProps: MeterBarChartProps;
  thresholdIndicators: number[];
  matchingThresholdColor?: string;
}

export const MeterBarChartProviders = ({
  containerWidth,
  matchingThresholdColor,
  meterBarChartInternal,
  meterBarChartProps,
  thresholdIndicators,
  children,
}: PropsWithChildren<MeterBarChartProvidersProps>) => {
  const meterBarChartData: MeterBarChartDataProviderData = {
    color:
      matchingThresholdColor ??
      meterBarChartProps.color ??
      SEGMENT_COLOR_DEFAULT,
    max: meterBarChartInternal.max,
    min: meterBarChartInternal.min,
    tooltip: meterBarChartInternal.tooltip,
    value: meterBarChartProps.value,
    width: containerWidth,
    label: meterBarChartInternal.labelSlot ?? '',
    name: meterBarChartProps.name ?? NAME_DEFAULT_VALUE,
    thresholdIndicators,
  };

  const contextSeriesActionsValue = useMemo(
    () => ({
      chartSeriesActions: meterBarChartProps.seriesActions as (
        series: unknown,
        datapoint?: unknown,
      ) => ReactElement,
    }),
    [meterBarChartProps.seriesActions],
  );

  return (
    <FormatterContext.Provider
      value={{ formatter: meterBarChartInternal.formatter }}
    >
      <MeterBarChartDataProvider data={meterBarChartData}>
        <ChartSeriesActionContext.Provider value={contextSeriesActionsValue}>
          {children}
        </ChartSeriesActionContext.Provider>
      </MeterBarChartDataProvider>
    </FormatterContext.Provider>
  );
};
